import { useEffect, useMemo, useRef, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useNewsData } from '../context/NewsDataContext';
import { pickTeEn } from '../utils/localizedContent.js';
import { formatTimeAgo } from '../utils/timeAgo';
import {
  STORY_CATEGORIES,
  resolveStoryCategoryId,
  storyCategoryLabel,
} from '../config/storyCategories.js';
import { OPEN_DAILY_PAPER_EVENT, OPEN_MONTHLY_PAPER_EVENT } from '../utils/paperCache';
import { maybeAlertNewEdition } from '../utils/editionAlerts';
import { useNativeTab } from './NativeTabContext';
import { mediaUrl, API_URL as API } from '../config/env.js';
import MagazineCover from './MagazineCover';
import './NativeHomeScreen.css';

const ALL = '__all__';
const LOCALE_MAP = { te: 'te-IN', en: 'en-IN', ta: 'ta-IN', kn: 'kn-IN' };

function formatRupee(value, lang) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  try {
    return new Intl.NumberFormat(LOCALE_MAP[lang] || 'en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: n >= 1000 ? 0 : 2,
      minimumFractionDigits: n >= 1000 ? 0 : 2,
    }).format(n);
  } catch {
    return `₹${n.toFixed(n >= 1000 ? 0 : 2)}`;
  }
}

function validFuelPrice(v, fallback) {
  const n = Number(v);
  if (Number.isFinite(n) && n > 0) return n;
  const f = Number(fallback);
  return Number.isFinite(f) && f > 0 ? f : null;
}

function marketLabel(id, kind, t, fallback) {
  if (id === 'gold' || kind === 'gold') return t.nativeMarketGold;
  if (id === 'silver' || kind === 'silver') return t.nativeMarketSilver;
  if (kind === 'petrol' || String(id).startsWith('petrol')) return t.nativeMarketPetrol;
  if (kind === 'diesel' || String(id).startsWith('diesel')) return t.nativeMarketDiesel;
  return fallback;
}

function MarketRatesStrip({ lang, t }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/market-rates`)
      .then((r) => r.json())
      .then((payload) => {
        if (!cancelled) setData(payload && Array.isArray(payload.items) ? payload : null);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const items = data?.items || [];
  const metals = items.filter((x) => x.id === 'gold' || x.id === 'silver');

  // Always show AP + Telangana fuel (API regions, or sensible defaults until server is updated).
  const FALLBACK_FUEL = [
    { id: 'tg', state: 'Telangana', city: 'Hyderabad', petrol: 115.72, diesel: 103.83 },
    { id: 'ap', state: 'Andhra Pradesh', city: 'Vijayawada', petrol: 116.7, diesel: 104.43 },
  ];

  const fuelRegions = FALLBACK_FUEL.map((fb) => {
    const fromApi = Array.isArray(data?.fuelRegions)
      ? data.fuelRegions.find((r) => r.id === fb.id)
      : null;
    const fromItemsPetrol =
      items.find((x) => x.id === `petrol-${fb.id}` || (x.kind === 'petrol' && x.region === fb.id))?.value ??
      (fb.id === 'tg' ? items.find((x) => x.id === 'petrol')?.value : null);
    const fromItemsDiesel =
      items.find((x) => x.id === `diesel-${fb.id}` || (x.kind === 'diesel' && x.region === fb.id))?.value ??
      (fb.id === 'tg' ? items.find((x) => x.id === 'diesel')?.value : null);

    return {
      id: fb.id,
      state: fromApi?.state || fb.state,
      city: fromApi?.city || fb.city,
      petrol: validFuelPrice(fromApi?.petrol ?? fromItemsPetrol, fb.petrol),
      diesel: validFuelPrice(fromApi?.diesel ?? fromItemsDiesel, fb.diesel),
    };
  });

  if (!metals.length && !fuelRegions.length) return null;

  return (
    <section className="news-markets" data-sub-free aria-label={t.nativeMarketsTitle}>
      <div className="news-card news-markets__card">
        <div className="news-card__head">
          <strong>{t.nativeMarketsTitle}</strong>
          <em>{t.nativeMarketsToday}</em>
        </div>

        {metals.length > 0 ? (
          <div className="news-markets__metals">
            {metals.map((item) => {
              const up = item.change != null && item.change > 0;
              const down = item.change != null && item.change < 0;
              return (
                <div key={item.id} className={`news-markets__metal news-markets__metal--${item.id}`}>
                  <span>{marketLabel(item.id, item.kind, t, item.label)}</span>
                  <strong>{formatRupee(item.value, lang)}</strong>
                  {item.change != null ? (
                    <b className={up ? 'is-up' : down ? 'is-down' : ''}>
                      {up ? '+' : down ? '−' : ''}
                      {Math.abs(item.change).toFixed(1)}%
                    </b>
                  ) : (
                    <b className="is-flat">/10g</b>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}

        {fuelRegions.length > 0 ? (
          <div className="news-markets__fuels">
            {fuelRegions.map((fr) => (
              <div key={fr.id} className="news-markets__fuel">
                <div className="news-markets__fuel-loc">
                  <strong>{fr.id === 'tg' ? t.nativeMarketTG || 'Telangana' : t.nativeMarketAP || 'Andhra'}</strong>
                  <em>{fr.city}</em>
                </div>
                <div className="news-markets__fuel-vals">
                  <span>
                    <i>{t.nativeMarketPetrol}</i>
                    <b>{fr.petrol != null && fr.petrol > 0 ? formatRupee(fr.petrol, lang) : '—'}</b>
                  </span>
                  <span>
                    <i>{t.nativeMarketDiesel}</i>
                    <b>{fr.diesel != null && fr.diesel > 0 ? formatRupee(fr.diesel, lang) : '—'}</b>
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function mediaSrc(path) {
  return mediaUrl(path);
}

function formatEditionDate(dateKey, lang) {
  if (!dateKey) return '';
  const d = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateKey;
  return d.toLocaleDateString(LOCALE_MAP[lang] || 'en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function formatMonthLabel(monthKey, lang) {
  if (!monthKey) return '';
  const [y, m] = monthKey.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(LOCALE_MAP[lang] || 'en-IN', {
    month: 'long',
    year: 'numeric',
  });
}

function storyMeta(s, lang) {
  const id = resolveStoryCategoryId(s);
  return {
    title: pickTeEn(lang, s.title, s.titleEn),
    category: id ? storyCategoryLabel(id, lang) : pickTeEn(lang, s.category, s.categoryEn),
    cover: mediaSrc(s.cardImage || s.image),
    time: formatTimeAgo(s.createdAt, lang),
  };
}

function Cover({ src, className }) {
  const [failed, setFailed] = useState(false);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    setFailed(false);
    setTries(0);
  }, [src]);
  if (!src || failed) return <span className={`${className} toi-ph`} aria-hidden />;
  const url = tries ? `${src}${src.includes('?') ? '&' : '?'}_rk=${tries}` : src;
  return (
    <img
      className={className}
      src={url}
      alt=""
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => {
        if (tries < 2) setTries((n) => n + 1);
        else setFailed(true);
      }}
    />
  );
}

function TopNewsSlides({ stories, lang, onOpen }) {
  const scroller = useRef(null);
  const [index, setIndex] = useState(0);
  const dragging = useRef(false);
  const count = stories.length;
  const ids = stories.map((s) => s.id).join(',');

  const scrollTo = (i, smooth = true) => {
    const el = scroller.current;
    const card = el?.children[i];
    if (!el || !card) return;
    const left = card.offsetLeft - (el.clientWidth - card.clientWidth) / 2;
    el.scrollTo({ left: Math.max(0, left), behavior: smooth ? 'smooth' : 'auto' });
  };

  useEffect(() => {
    setIndex(0);
    scrollTo(0, false);
  }, [ids]);

  useEffect(() => {
    if (count <= 1) return undefined;
    const t = window.setInterval(() => {
      if (dragging.current) return;
      const next = (index + 1) % count;
      setIndex(next);
      scrollTo(next);
    }, 5600);
    return () => window.clearInterval(t);
  }, [count, ids, index]);

  if (!count) return null;

  const syncIndex = () => {
    const el = scroller.current;
    if (!el) return;
    const mid = el.scrollLeft + el.clientWidth / 2;
    let best = 0;
    let dist = Infinity;
    [...el.children].forEach((card, i) => {
      const center = card.offsetLeft + card.offsetWidth / 2;
      const d = Math.abs(center - mid);
      if (d < dist) {
        dist = d;
        best = i;
      }
    });
    setIndex(best);
  };

  return (
    <div className="news-slides">
      <div
        ref={scroller}
        className="news-slides__track"
        onScroll={syncIndex}
        onTouchStart={() => {
          dragging.current = true;
        }}
        onTouchEnd={() => {
          window.setTimeout(() => {
            dragging.current = false;
          }, 80);
        }}
      >
        {stories.map((s) => {
          const d = storyMeta(s, lang);
          return (
            <button key={s.id} type="button" className="news-hero news-hero--slide" onClick={() => onOpen(s.id)}>
              <Cover src={d.cover} className="news-hero__img" />
              <div className="news-hero__copy">
                <p>{d.category}</p>
                <h2>{d.title}</h2>
                {d.time ? <time>{d.time}</time> : null}
              </div>
            </button>
          );
        })}
      </div>
      {count > 1 ? (
        <div className="news-slides__dots">
          {stories.map((s, i) => (
            <button
              key={s.id}
              type="button"
              className={i === index ? 'is-on' : ''}
              aria-label={`Story ${i + 1}`}
              onClick={() => {
                setIndex(i);
                scrollTo(i);
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function StoryRow({ story, lang, onOpen }) {
  const d = storyMeta(story, lang);
  return (
    <button type="button" className="news-row" onClick={() => onOpen(story.id)}>
      <div className="news-row__copy">
        <h3>{d.title}</h3>
        <p>
          {d.category}
          {d.time ? ` · ${d.time}` : ''}
        </p>
      </div>
      <Cover src={d.cover} className="news-row__img" />
    </button>
  );
}

export default function NativeHomeScreen({ onOpenStory }) {
  const { t, lang } = useLang();
  const { homeFeed, stories } = useNewsData();
  const { setTab } = useNativeTab();
  const [cat, setCat] = useState(ALL);
  const [latestMonthly, setLatestMonthly] = useState(null);

  const latestDaily = homeFeed.latestPaperDate;
  const dailyLabel = formatEditionDate(latestDaily, lang);
  const monthLabel = formatMonthLabel(latestMonthly, lang);

  useEffect(() => {
    fetch(`${API}/api/monthly-papers`)
      .then((r) => r.json())
      .then((d) => {
        if (d.months?.length) setLatestMonthly(d.months[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!latestDaily || !homeFeed.editionLive) return;
    void maybeAlertNewEdition({
      dateKey: latestDaily,
      title: t.editionNotifyTitle,
      body: t.editionNotifyBody,
    });
  }, [latestDaily, homeFeed.editionLive, t.editionNotifyTitle, t.editionNotifyBody]);

  const list = useMemo(() => {
    const rows = stories || [];
    if (cat === ALL) return rows;
    return rows.filter((s) => resolveStoryCategoryId(s) === cat);
  }, [stories, cat]);

  const featuredSlides = useMemo(() => {
    if (cat !== ALL) return list.slice(0, 1);
    const tops = homeFeed.topStories?.length ? homeFeed.topStories : list;
    return (tops || []).slice(0, 6);
  }, [cat, list, homeFeed.topStories]);
  const slideIds = new Set(featuredSlides.map((s) => s.id));
  const rest = cat === ALL ? list.filter((s) => !slideIds.has(s.id)) : list.slice(1);

  const openDaily = () => {
    setTab('paper');
    if (!latestDaily) return;
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent(OPEN_DAILY_PAPER_EVENT, { detail: { dateKey: latestDaily } }));
    }, 40);
  };

  const openMonthly = () => {
    setTab('paper');
    if (!latestMonthly) return;
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent(OPEN_MONTHLY_PAPER_EVENT, { detail: { monthKey: latestMonthly } }));
    }, 40);
  };

  return (
    <div className="news-home">
      <section className="news-editions" aria-label={t.navEpaper}>
        <div className="news-card news-editions__card">
          <button
            type="button"
            className="news-editions__row"
            onClick={openDaily}
            disabled={!latestDaily}
          >
            <span className="news-editions__icon news-editions__icon--daily" aria-hidden>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="4" y="5" width="16" height="15" rx="2.2" />
                <path d="M8 3.5v3M16 3.5v3M4 10h16" />
              </svg>
            </span>
            <span className="news-editions__text">
              <strong>{t.dailyEdition}</strong>
              <em>{dailyLabel || t.nativeToday}</em>
            </span>
            <span className="news-editions__chev" aria-hidden>
              <svg viewBox="0 0 12 20" width="8" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 2l8 8-8 8" />
              </svg>
            </span>
          </button>

          <button
            type="button"
            className="news-editions__row"
            onClick={openMonthly}
            disabled={!latestMonthly}
          >
            <span className="news-editions__thumb" aria-hidden>
              <MagazineCover monthKey={latestMonthly} fill />
            </span>
            <span className="news-editions__text">
              <strong>{t.monthlyHeadline}</strong>
              <em>{monthLabel || '—'}</em>
            </span>
            <span className="news-editions__chev" aria-hidden>
              <svg viewBox="0 0 12 20" width="8" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 2l8 8-8 8" />
              </svg>
            </span>
          </button>
        </div>
      </section>

      <div className="news-chips" data-sub-free>
        <button type="button" className={cat === ALL ? 'is-on' : ''} onClick={() => setCat(ALL)}>
          {t.toiTopNews}
        </button>
        {STORY_CATEGORIES.map((c) => (
          <button
            key={c.id}
            type="button"
            className={cat === c.id ? 'is-on' : ''}
            onClick={() => setCat(c.id)}
          >
            {c[lang] || c.en}
          </button>
        ))}
      </div>

      <MarketRatesStrip lang={lang} t={t} />

      {featuredSlides.length > 0 && (
        <TopNewsSlides stories={featuredSlides} lang={lang} onOpen={onOpenStory} />
      )}

      <ul className="news-feed">
        {rest.map((s) => (
          <li key={s.id}>
            <StoryRow story={s} lang={lang} onOpen={onOpenStory} />
          </li>
        ))}
      </ul>
    </div>
  );
}
